export type V2UserGroup =
  | 'university'
  | 'repeat_applicant'
  | 'general'
  | 'high_school'
  | 'middle_school'
  | 'elementary'

export type LegacySurveyUserGroup =
  | 'college'
  | 'repeater'
  | 'general'
  | 'highschool'
  | 'middleschool'
  | 'elemantaryschool'
  | 'generalCollege'

export interface LearningKeyword {
  id: number
  name: string
}

export interface LearningKeywordSearchResult extends LearningKeyword {
  depth: number
  pathName: string
}

export interface SelectableLearningKeyword {
  id: number
  code: string
  name: string
  type: string
  keyWords: string
  manageHostName: string
}

export interface SurveySubmitInput {
  selectedKeywords: LearningKeyword[]
  examCustomNames: string[]
  userGroup: LegacySurveyUserGroup
  userGroupGrade: number | null
}

export interface SurveySubmitRequest {
  examKeywordIds: number[]
  examCustomNames: string[]
  userGroup: V2UserGroup
  userGroupGrade?: number | null
}

export function buildSurveySubmitRequest(input: SurveySubmitInput): SurveySubmitRequest {
  const userGroup = toV2UserGroup(input.userGroup)
  const userGroupGrade = getValidUserGroupGrade(userGroup, input.userGroupGrade)
  validateExamKeywords(input.selectedKeywords, input.examCustomNames)

  return {
    examKeywordIds: input.selectedKeywords.map((keyword) => keyword.id),
    examCustomNames: input.examCustomNames,
    userGroup,
    ...(userGroupGrade === null ? {} : { userGroupGrade }),
  }
}

function validateExamKeywords(
  selectedKeywords: LearningKeyword[],
  examCustomNames: string[],
): void {
  if (selectedKeywords.length + examCustomNames.length > 4) {
    throw new Error('LEARNING_DATA_EXAM_KEYWORD_LIMIT_EXCEEDED')
  }

  if (examCustomNames.some((name) => name.length > 30)) {
    throw new Error('LEARNING_DATA_EXAM_CUSTOM_NAME_TOO_LONG')
  }
}

function getValidUserGroupGrade(userGroup: V2UserGroup, grade: number | null): number | null {
  switch (userGroup) {
    case 'elementary':
      return validateGrade(grade, 1, 6)
    case 'middle_school':
    case 'high_school':
      return validateGrade(grade, 1, 3)
    case 'university':
    case 'repeat_applicant':
    case 'general':
      return null
  }
}

function validateGrade(grade: number | null, min: number, max: number): number {
  if (grade === null) {
    throw new Error('LEARNING_DATA_GRADE_REQUIRED')
  }

  if (!Number.isInteger(grade) || grade < min || grade > max) {
    throw new Error('LEARNING_DATA_GRADE_INVALID')
  }

  return grade
}

export function toV2UserGroup(userGroup: LegacySurveyUserGroup): V2UserGroup {
  switch (userGroup) {
    case 'college':
    case 'generalCollege':
      return 'university'
    case 'repeater':
      return 'repeat_applicant'
    case 'general':
      return 'general'
    case 'highschool':
      return 'high_school'
    case 'middleschool':
      return 'middle_school'
    case 'elemantaryschool':
      return 'elementary'
  }
}

export function toLegacySurveyUserGroup(userGroup: V2UserGroup | null): LegacySurveyUserGroup | null {
  switch (userGroup) {
    case null:
      return null
    case 'university':
      return 'college'
    case 'repeat_applicant':
      return 'repeater'
    case 'general':
      return 'general'
    case 'high_school':
      return 'highschool'
    case 'middle_school':
      return 'middleschool'
    case 'elementary':
      return 'elemantaryschool'
  }
}

export function toSelectableLearningKeyword(
  keyword: LearningKeywordSearchResult,
): SelectableLearningKeyword {
  return {
    id: keyword.id,
    code: `${keyword.id}`,
    name: keyword.name,
    type: '',
    keyWords: keyword.pathName,
    manageHostName: '',
  }
}
